import React from "react";
import { Box, Typography, useTheme, SxProps, Theme } from "@mui/material";

type Props = {
  label?: string;
  thicknessPct?: number;
  sx?: SxProps<Theme>;
};

const NoDataCircle: React.FC<Props> = ({
  label = "No data",
  thicknessPct = 12,
  sx,
}) => {
  const theme = useTheme();

  const ring = theme.palette.mode === "dark" ? "#3a3d46" : "#e5e7eb";
  const bg = theme.palette.mode === "dark" ? "#13151a" : "#ffffff";
  const text = theme.palette.text.secondary;

  const strokeWidth = Math.max(1, Math.min(50, thicknessPct));
  const r = 50 - strokeWidth / 2;

  const holePct = 100 - 2.5 * strokeWidth;
  const padPct = strokeWidth * 0.4;

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: "1 / 1",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@supports not (aspect-ratio: 1 / 1)": {
          height: 0,
          paddingTop: "100%",
        },
        ...sx,
      }}
    >
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label={label}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={ring}
          strokeWidth={strokeWidth}
        />
        <circle cx="50" cy="50" r={50 - strokeWidth * 1.25} fill={bg} />
      </svg>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Typography
          component="div"
          sx={{
            width: `${holePct}%`,
            padding: `${padPct}%`,
            textAlign: "center",
            lineHeight: 1.2,
            color: theme.palette.text.secondary,
            fontSize: "clamp(12px, 2.8vw, 13px)",
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

export default NoDataCircle;
